// storage-defaults.util.ts
import { Unit } from '../enums/unit.enum';
import { Ingredient } from '../models/ingredient.model';
import { IngredientCategory } from '../enums/ingredient-category.enum';
import { IngredientLocation } from '../enums/ingredient-location.enum';
import { getUnitsForSystem } from '../enums/unit.enum';
import { getUserUnitSystem } from '../utils/unit-settings.util';

type DefaultSuggestion = {
  unit: Unit;
  quantity: number;
  location?: IngredientLocation | null;
};

// ---------- helpers for Dairy & Eggs subtypes ----------
function isEgg(ing: Ingredient): boolean {
  const text = `${ing.name ?? ''} ${ing.hu_name ?? ''}`.toLowerCase();
  return ing.icon === 'egg' || /egg|toj[aá]s/.test(text);
}
function isCheese(ing: Ingredient): boolean {
  const text = `${ing.name ?? ''} ${ing.hu_name ?? ''}`.toLowerCase();
  return ing.icon === 'cheese' || /cheese|sajt/.test(text);
}
function isMilkOrDairyDrink(ing: Ingredient): boolean {
  const text = `${ing.name ?? ''} ${ing.hu_name ?? ''}`.toLowerCase();
  return /milk|tej|cream|tejsz[ií]n|yog(h)?urt|joghurt|kefir/.test(text);
}

function isSmallBakingItem(ing: Ingredient): boolean {
  const text = `${ing.name ?? ''} ${ing.hu_name ?? ''}`.toLowerCase();
  return /baking powder|sütőpor|sutopor|baking soda|szódabikarbóna|szodabikarbona|yeast|élesztő|eleszto/.test(text);
}

// ---------- pack defaults (BASE in grams / mL) ----------
const MASS_DEFAULT: Record<string, number> = {
  [IngredientCategory.PastaAndRice]: 500,
  [IngredientCategory.Baking]: 1000,
  [IngredientCategory.DriedFruitsAndNuts]: 100,
  [IngredientCategory.HerbsAndSpices]: 20,
  [IngredientCategory.Cereals]: 250,
  [IngredientCategory.SweetsAndSnacks]: 100,
  [IngredientCategory.SpreadAndDips]: 250,
  [IngredientCategory.Meat]: 500,
  [IngredientCategory.FishAndSeafood]: 400,
  [IngredientCategory.DairyAndEggs]: 200, // generic cheese/block default; eggs handled as pieces below
  [IngredientCategory.FrozenFood]: 500
};

// DairyAndEggs removed here (milk/cream handled by name)
const VOL_DEFAULT: Record<string, number> = {
  [IngredientCategory.DairyAlternatives]: 1000,
  [IngredientCategory.OilVinegarSauces]: 500,
  [IngredientCategory.Beverages]: 1000
};

const PIECES_DEFAULT: Record<string, number> = {
  [IngredientCategory.Fruits]: 4,
  [IngredientCategory.Vegetables]: 4,
  [IngredientCategory.BreadAndBakery]: 1,
  [IngredientCategory.CannedGoods]: 1
};

function prefersVolume(cat?: IngredientCategory): boolean {
  return !!cat && [
    IngredientCategory.Beverages,
    IngredientCategory.DairyAlternatives,
    IngredientCategory.OilVinegarSauces,
  ].includes(cat);
}

/* ───────────── Renderers (base → user units) ───────────── */
function renderMassToUserUnits(
  grams: number,
  allowed: Unit[]
): { unit: Unit; qty: number } {
  // US available?
  const hasLb  = allowed.includes(Unit.lbs);
  const hasOz  = allowed.includes(Unit.ounces);
  const hasKg  = allowed.includes(Unit.kilograms);
  const hasG   = allowed.includes(Unit.grams);

  if (hasLb || hasOz) {
    if (grams >= 453.592 && hasLb) {
      return { unit: Unit.lbs, qty: round1(grams / 453.592) };
    }
    // fallback to oz if present, else lb anyway
    if (hasOz) return { unit: Unit.ounces, qty: round1(grams / 28.3495) };
    return { unit: Unit.lbs, qty: round1(grams / 453.592) };
  }

  // Metric
  if (grams >= 1000 && hasKg) {
    return { unit: Unit.kilograms, qty: round1(grams / 1000) };
  }
  if (hasG) return { unit: Unit.grams, qty: round1(grams) };

  // Last resort: pick the first allowed if neither g nor kg is present
  return { unit: allowed[0], qty: round1(grams) };
}

function renderVolumeToUserUnits(
  ml: number,
  allowed: Unit[]
): { unit: Unit; qty: number } {
  const hasCups = allowed.includes(Unit.cups);
  const hasFlOz = allowed.includes(Unit.fl_oz);
  const hasL    = allowed.includes(Unit.litres);
  const hasMl   = allowed.includes(Unit.millilitres);

  // US
  if (hasCups || hasFlOz) {
    if (ml >= 240 && hasCups) {
      return { unit: Unit.cups, qty: round1(ml / 240) };
    }
    if (hasFlOz) return { unit: Unit.fl_oz, qty: round1(ml / 29.5735) };
    return { unit: Unit.cups, qty: round1(ml / 240) };
  }

  // Metric
  if (ml >= 1000 && hasL) {
    return { unit: Unit.litres, qty: round1(ml / 1000) };
  }
  if (hasMl) return { unit: Unit.millilitres, qty: round1(ml) };

  // Last resort: pick the first allowed if neither mL nor L is present
  return { unit: allowed[0], qty: round1(ml) };
}

function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/* ───────────── Main logic ───────────── */
export function suggestStorageDefaults(ing: Ingredient): DefaultSuggestion {
  const allowedUnits = getUnitsForSystem(getUserUnitSystem());

  // 1) Eggs → pieces (common EU 10-pack)
  if (isEgg(ing) && allowedUnits.includes(Unit.pieces)) {
    return { unit: Unit.pieces, quantity: 10, location: ing.default_location ?? null };
  }

  // 2) Dairy & Eggs split:
  if (ing.category === IngredientCategory.DairyAndEggs) {
    // Milk/cream/yoghurt → volume (1 L base → render to user)
    if (isMilkOrDairyDrink(ing)) {
      const ml = 1000;
      const { unit, qty } = renderVolumeToUserUnits(ml, allowedUnits);
      return { unit, quantity: qty, location: ing.default_location ?? null };
    }
    // Cheese → mass (200 g base → render to user)
    if (isCheese(ing)) {
      const g = 200;
      const { unit, qty } = renderMassToUserUnits(g, allowedUnits);
      return { unit, quantity: qty, location: ing.default_location ?? null };
    }
    // Otherwise generic Dairy & Eggs default (mass 200 g base)
    {
      const g = MASS_DEFAULT[String(ing.category)] ?? 200;
      const { unit, qty } = renderMassToUserUnits(g, allowedUnits);
      return { unit, quantity: qty, location: ing.default_location ?? null };
    }
  }

  // 2b) Small baking items like baking powder should start at a realistic package size.
  if (ing.category === IngredientCategory.Baking && isSmallBakingItem(ing)) {
    const g = 10;
    const { unit, qty } = renderMassToUserUnits(g, allowedUnits);
    return { unit, quantity: qty, location: ing.default_location ?? null };
  }

  // 3) Generic volume-first categories
  if (prefersVolume(ing.category) || VOL_DEFAULT[String(ing.category)] !== undefined) {
    const ml = VOL_DEFAULT[String(ing.category)] ?? 1000;
    const { unit, qty } = renderVolumeToUserUnits(ml, allowedUnits);
    return { unit, quantity: qty, location: ing.default_location ?? null };
  }

  // 4) Generic mass categories
  if (MASS_DEFAULT[String(ing.category)] !== undefined) {
    const g = MASS_DEFAULT[String(ing.category)];
    const { unit, qty } = renderMassToUserUnits(g, allowedUnits);
    return { unit, quantity: qty, location: ing.default_location ?? null };
  }

  // 5) Pieces-first categories
  if (PIECES_DEFAULT[String(ing.category)] !== undefined && allowedUnits.includes(Unit.pieces)) {
    return {
      unit: Unit.pieces,
      quantity: PIECES_DEFAULT[String(ing.category)],
      location: ing.default_location ?? null
    };
  }

  // 6) Fallback: mass 500 g base → render to user
  {
    const g = 500;
    const { unit, qty } = renderMassToUserUnits(g, allowedUnits);
    return { unit, quantity: qty, location: ing.default_location ?? null };
  }
}
